import { FILTERS_NAME } from '@va/constants';
import { useTranslate } from '@va/localization';
import { TextFilter } from '@va/shared/feature-filters';
import { ApiOperators, buildNestedFiltersPage, FilterOperators } from '@va/types/filters';
import { useMemo } from 'react';

type UTMFilterName =
  | 'adCampaignLabel'
  | 'adCampaignSource'
  | 'adCampaignMedium'
  | 'adCampaignTerm'
  | 'adCampaignContent'
  | 'adCampaignSourcePlatform'
  | 'adCampaignCreativeFormat'
  | 'adCampaignMarketingTactic';

export const useUTMFilters = () => {
  const utmCampaignLabel = useUTMFilter('adCampaignLabel');
  const adCampaignSource = useUTMFilter('adCampaignSource');
  const adCampaignMedium = useUTMFilter('adCampaignMedium');
  const adCampaignTerm = useUTMFilter('adCampaignTerm');
  const adCampaignContent = useUTMFilter('adCampaignContent');
  const adCampaignSourcePlatform = useUTMFilter('adCampaignSourcePlatform');
  const adCampaignCreativeFormat = useUTMFilter('adCampaignCreativeFormat');
  const adCampaignMarketingTactic = useUTMFilter('adCampaignMarketingTactic');

  const filters = useMemo(
    () => [
      utmCampaignLabel,
      adCampaignSource,
      adCampaignMedium,
      adCampaignTerm,
      adCampaignContent,
      adCampaignSourcePlatform,
      adCampaignCreativeFormat,
      adCampaignMarketingTactic,
    ],
    [
      adCampaignContent,
      adCampaignCreativeFormat,
      adCampaignMarketingTactic,
      adCampaignMedium,
      adCampaignSource,
      adCampaignSourcePlatform,
      adCampaignTerm,
      utmCampaignLabel,
    ],
  );

  return filters;
};

const useUTMFilter = (name: UTMFilterName, params?: ConstructorParameters<typeof TextFilter>[0]) => {
  const translate = useTranslate();

  return new TextFilter({
    name: name,
    id: name,
    label: translate(`filters.utmParams.${name}`),
    operator: FilterOperators.contains,
    allowMultiSelect: true,
    operatorOptions: [FilterOperators.contains, FilterOperators.doesNotContain, FilterOperators.is],
    operatorMapper: (operator) => {
      switch (operator) {
        case FilterOperators.contains:
          return ApiOperators.in;
        case FilterOperators.doesNotContain:
          return ApiOperators.notIn;
        case FilterOperators.is:
          return ApiOperators.eq;
        default:
          return ApiOperators.in;
      }
    },
    ...params,
    inputProps: { placeholder: translate(`filters.placeholder.${name}`), ...params?.inputProps },
  });
};

export const useUtmParametersFilters = () => {
  const translate = useTranslate();

  const utmFilters = useUTMFilters();

  return buildNestedFiltersPage({
    label: translate('filters.utmParameters'),
    filters: utmFilters,
    id: 'utm-filters',
    name: FILTERS_NAME.UTM_PARAMETERS,
  });
};
